/**
* @file CreateEditForm.js
 
* @author - Team E (Meeramol Mathew, Krishnapriya N, Anju Sebastian)
* @sprint - B2W-Sprint-1
* @description -
* This component is a modal form used to create or update an item.
* It includes fields for selecting an item, picking a date, entering ticket number, adding comments, and uploading attachments.
*/
 
import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, DatePicker, Upload, Button,message } from "antd";
import { UploadOutlined } from '@ant-design/icons';
import 'antd/dist/reset.css';
import { selectOptions } from "../../data/Offboarding/selectOptions"; // Import the reusable list of options
import moment from 'moment';
 
/**
 * CreateForm Component
 *  
 * Props:
 * @param {boolean} visible - Controls the visibility of the modal.
 * @param {function} setVisible - Function to set the visibility of the modal.
 * @param {function} onCreate - Callback function for creating a new record.
 * @param {function} onUpdate - Callback function for updating an existing record.
 * @param {object} selectedRecord - The record being edited (if any).
 */
 
const CreateForm = ({ visible, setVisible, onCreate, onUpdate, selectedRecord }) => {
  const [form] = Form.useForm();
  const [isUpdated, setIsUpdated] = useState(false);
 
  // Effect to handle form data population on record selection
  useEffect(() => {
    if (selectedRecord) {
      form.setFieldsValue({
        ...selectedRecord,
        date: moment(selectedRecord.date, 'DD-MM-YYYY'), // Convert the string to moment for DatePicker
      });
      setIsUpdated(false); // Reset update state on edit
    } else {
      form.resetFields(); // Reset form fields when there's no selected record
    }
  }, [form, selectedRecord]);
 
  /**
  * Handles form submission.
  * Validates and collects form values, formats the data, and triggers the appropriate callback.
  */
  const handleCreate = () => {
    form.validateFields()
      .then((values) => {
        // form.resetFields();
        const selectedOption = selectOptions.find(option => option.value === values.item);
        const jsonData = {
          ...values,
          actionItem: selectedOption ? selectedOption.label : '', // Include the label
          date: values.date.format('DD-MM-YYYY'), // Format the date
 
          key: selectedRecord ? selectedRecord.key : Date.now()
        };
 
 
        if (selectedRecord) {
          onUpdate(jsonData,selectedRecord);  // Update existing record, Pass values to parent function
 
        }
        else {
          onCreate(jsonData);  // Create new record, Pass values to parent function
          form.resetFields();
 
        }
        setIsUpdated(false); // Reset the updated state after submitting
      })
      .catch((info) => {
        console.log("Validation Failed:", info);
      });
  };
  /**
  * Handles file upload (currently prevents auto-upload).
  * @param {File} file - The uploaded file.
  * @returns {boolean} Prevents automatic file upload.
  */
  const handleUpload = (file) => {
    const isSmallEnough = file.size <= 20 * 1024; // Check if the file size is <= 20 KB
    console.log(isSmallEnough);
    if (isSmallEnough) {
      
      return false; // Prevent the upload if the file is too large
    }
    message.error('File must be smaller than 20 KB!');
    return Upload.LIST_IGNORE; // Allow upload if the file is within the size limit
  };
 
  /**
   * Handles modal close action.
   * Resets form if no record is selected and closes the modal.
   */
  const handleCancel = () => {
    if (!selectedRecord) {
      form.resetFields(); // Clear all form fields
    }
 
    setVisible(false); // Close modal
  };
 
  /**
   * Handles form field changes.
   * Enables the update button when a field is modified.
   */
  const handleValuesChange = () => {
    // Check if any field value is changed, if so, enable the Update button
    setIsUpdated(true);
  };
 
  // Set modal height to be less than the window height
  const modalHeight = window.innerHeight * 0.8; // 80% of the window height
 
  return (
    <Modal
      open={visible}
 
      title={
 
        <div className=" pt-2 pb-2 px-4 pl-0 rounded-lg sticky top-0 bg-white z-10">
          {/* <span className="inline-block text-primary p-2 pl-0 text-xl"> */}
          <span className="inline-block p-2 pl-0 text-xl" style={{ color: 'rgb(44, 87, 120)' }}> 
            {selectedRecord ? 'Edit Item' : 'Add New Item'}
          </span>
        </div>
      }
 
 
      okText={selectedRecord ? 'Update' : 'Submit'}
      onCancel={handleCancel}  // Use handleCancel to reset fields and close modal
 
      onOk={handleCreate}  // Submit form on OK
      okButtonProps={{
        className: "text-white border p-4 bg-[var(--color-primary)]",
        style: !isUpdated ? {
                  color: 'white', 
                  cursor: 'not-allowed', // Change the cursor to indicate the button is disabled
                  opacity: 0.6 // Optional: Make the button appear slightly faded
                } : {},
        disabled: !isUpdated, // Disable the button if nothing has been updated
      }}
      cancelButtonProps={{
        className: "text-white border p-4 bg-[var(--color-primary)]"
 
      }}
      style={{
        maxHeight: `${modalHeight}px`,
        overflowY: 'hidden', // Prevents the modal itself from scrolling
      }}
      maskClosable={false} // Prevents closing on outside click
    >
      <div className="modal-content-wrapper" style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
      <Form form={form} layout="vertical" onValuesChange={handleValuesChange}   >
 
        {/* Item Selection Field */}
        <Form.Item name="item"
          label={<span style={{ color: 'rgb(44, 87, 120)', fontWeight: 'bold' }}>Item</span>}
 
          rules={[{ required: true, message: "Please select an item!" }]}>
          <Select placeholder="Select an item">
            {selectOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
 
        {/* DatePicker Field */}
        <Form.Item
          label={<span style={{ color: 'rgb(44, 87, 120)', fontWeight: 'bold' }}>Date</span>}
          name="date"
          rules={[{ required: true, message: "Please select a date!" }]}
        >
          <DatePicker
            className="w-full"
            format="DD-MM-YYYY"
            disabledDate={(current) => current && current > new Date()}
            inputReadOnly={true}  // This disables manual editing
          />
        </Form.Item>
 
        {/* Ticket Number Field */}
        <Form.Item
          name="ticketNumber"
          label={<span style={{ color: 'rgb(44, 87, 120)', fontWeight: 'bold' }}>Ticket Number</span>}
          rules={[
            {
              pattern: /^[a-zA-Z0-9]+$/,
              message: "Ticket Number must be alphanumeric!"
            }
          ]}
        >
          <Input
            className="w-full uppercase"  // Tailwind's 'uppercase' class to style the input text
          />
        </Form.Item>
 
        {/* Comments Field */}
        <Form.Item
          name="comments"
          // label={<span className="text-[rgb(44, 87, 120)] font-bold">Comment</span>}
          label={<span style={{ color: 'rgb(44, 87, 120)', fontWeight: 'bold' }}>Comment</span>}
          rules={[{ required: true, message: "Please input your comment!" },
          { max: 500, message: "Comment cannot exceed 500 characters!" }
          ]}
        >
          <Input.TextArea className="w-full capitalize" />
        </Form.Item>
 
        {/* Attachment Upload Field */}
        <Form.Item
          name="attachment"
          label={<span style={{ color: 'rgb(44, 87, 120)', fontWeight: 'bold' }}>Attachment</span>}
          valuePropName="fileList"
          getValueFromEvent={e => e && e.fileList}
          rules={[{ required: true, message: "Please upload an attachment!" }]}
        >
          
          <Upload
            beforeUpload={handleUpload}
            multiple={false} // Set to true if you want to allow multiple file uploads
            showUploadList={{ showRemoveIcon: true }} // Display remove icon on uploaded files
          >
            <Button
              icon={<UploadOutlined />}
              className="text-white border p-4 bg-[var(--color-primary)]"
            >
              Click to Upload
            </Button>
          </Upload>
        </Form.Item>
 
 
      </Form>
      </div>
     
    </Modal>
  );
};
 
export default CreateForm;